/**
 * Opening-hour slot grid for sports centers (aligned with admin SlotCalendar rolling logic).
 */

export const ROLLING_AVAILABILITY_DAYS = 366

/** Whether the center accepts bookings (admin field: bookingEnabled). */
export const isCenterBookingEnabled = (center) => {
  const value = center?.bookingEnabled
  if (value === true || value === 'true' || value === 1 || value === '1') return true
  return false
}

export const formatDateKey = (date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export const parseDateKeyLocal = (dateStr) => {
  const parts = String(dateStr || '').split('-').map(Number)
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null
  return new Date(parts[0], parts[1] - 1, parts[2])
}

/** Aligns with admin SlotCalendar — compare booking times to hourly slots */
export const normalizeTimeString = (value) => {
  if (value == null || typeof value !== 'string') return ''
  const trimmed = value.trim()
  const parts = trimmed.split(':')
  if (parts.length < 2) return trimmed
  const h = Math.min(23, Math.max(0, parseInt(parts[0], 10) || 0))
  const m = Math.min(59, Math.max(0, parseInt(parts[1], 10) || 0))
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Keep in sync with giochiamo-admin-site/src/utils/centerHours.js */
const parseHour = (timeStr) => {
  const part = String(timeStr ?? '').trim().split(':')[0]
  const h = Number(part)
  if (!Number.isFinite(h)) return 0
  return Math.min(23, Math.max(0, h))
}

const formatHourSlot = (hour) => `${String(hour).padStart(2, '0')}:00`

export const generateHourlyTimes = (openingTime, closingTime) => {
  const openHour = parseHour(openingTime || '08:00')
  const closeHour = parseHour(closingTime || '22:00')
  const times = []

  if (closeHour > openHour) {
    for (let h = openHour; h < closeHour; h++) {
      times.push(formatHourSlot(h))
    }
    return times
  }

  if (closeHour < openHour) {
    for (let h = openHour; h <= 23; h++) {
      times.push(formatHourSlot(h))
    }
    for (let h = 0; h < closeHour; h++) {
      times.push(formatHourSlot(h))
    }
    return times
  }

  if (openHour === 0) {
    for (let h = 0; h <= 23; h++) {
      times.push(formatHourSlot(h))
    }
    return times
  }

  for (let h = openHour; h <= 23; h++) {
    times.push(formatHourSlot(h))
  }
  for (let h = 0; h < openHour; h++) {
    times.push(formatHourSlot(h))
  }
  return times
}

/** Slots on `dateKey` that are still after `now` (for today); future dates return full hourly list */
export const getFutureSlotsForDate = (dateKey, hourlyTimes, now = new Date()) => {
  if (!dateKey || !Array.isArray(hourlyTimes)) return []
  if (formatDateKey(now) !== dateKey) return hourlyTimes
  return hourlyTimes.filter((time) => {
    const [h, m] = time.split(':').map(Number)
    const slotDate = parseDateKeyLocal(dateKey)
    if (!slotDate) return false
    slotDate.setHours(h, m || 0, 0, 0)
    return slotDate > now
  })
}

/** Active pitches at center — each can host one booking per hour */
export const getPitchCapacity = (center) =>
  Math.max(1, Array.isArray(center?.pitches) ? center.pitches.length : 0)

export const countBookingsForDateTime = (bookings, dateKey, normalizedTime) => {
  if (!Array.isArray(bookings) || !dateKey || !normalizedTime) return 0
  return bookings.filter(
    (b) =>
      b.date === dateKey &&
      normalizeTimeString(b.time) === normalizedTime &&
      b.status !== 'cancelled'
  ).length
}

export const pitchIdsEqual = (a, b) => String(a ?? '') === String(b ?? '')

/**
 * Whether this pitch already has a non-cancelled booking at date + time (admin SlotCalendar rule: one booking per pitch per slot).
 */
export const isTimeBookedOnPitch = (bookings, dateKey, normalizedTime, pitchId) => {
  if (!Array.isArray(bookings) || !dateKey || !normalizedTime) return false
  if (pitchId == null || pitchId === '') return false
  return bookings.some(
    (b) =>
      pitchIdsEqual(b.pitchId, pitchId) &&
      b.date === dateKey &&
      normalizeTimeString(b.time) === normalizedTime &&
      b.status !== 'cancelled'
  )
}

/**
 * Hourly slots for a date after applying opening hours, rolling window, and “today” future-only filter.
 * Returns [] if the date is invalid, in the past, or outside the rolling window.
 */
export const getBaseAvailableHourlySlots = (center, dateKey, now = new Date()) => {
  const hourly = generateHourlyTimes(center?.openingTime, center?.closingTime)
  if (!hourly.length || !dateKey) return []
  const dayDate = parseDateKeyLocal(dateKey)
  if (!dayDate) return []
  const dayStart = new Date(dayDate)
  dayStart.setHours(0, 0, 0, 0)
  const todayStart = new Date(now)
  todayStart.setHours(0, 0, 0, 0)
  if (dayStart < todayStart) return []
  const lastAllowed = new Date(todayStart)
  lastAllowed.setDate(todayStart.getDate() + ROLLING_AVAILABILITY_DAYS - 1)
  if (dayStart > lastAllowed) return []
  return getFutureSlotsForDate(dateKey, hourly, now)
}

/**
 * Bookable slot times for a calendar date: past days or outside the rolling window return [].
 * Aggregates across pitches: a slot is shown only if fewer bookings exist than pitch count (legacy / summary).
 * @param {{ bookings?: Array<{ date?: string, time?: string, status?: string }> }} [options]
 */
export const getAvailableSlotsForDateKey = (center, dateKey, now = new Date(), options = {}) => {
  const { bookings = [] } = options
  const slots = getBaseAvailableHourlySlots(center, dateKey, now)
  if (!slots.length) return []
  const capacity = getPitchCapacity(center)
  if (!bookings.length || capacity <= 0) return slots
  return slots.filter((time) => {
    const n = normalizeTimeString(time)
    return countBookingsForDateTime(bookings, dateKey, n) < capacity
  })
}

/**
 * Free hourly slots per pitch: excludes a time on a pitch if that pitch already has a non-cancelled booking.
 * When the center has no pitches array, returns one row using aggregate capacity (same as getAvailableSlotsForDateKey).
 *
 * @returns {{ pitchId: string | null, pitchName: string | null, sport: string, times: string[] }[]}
 */
export const getSlotsPerPitchForDateKey = (center, dateKey, now = new Date(), bookings = []) => {
  const baseSlots = getBaseAvailableHourlySlots(center, dateKey, now)
  if (!baseSlots.length) return []

  const pitches = Array.isArray(center?.pitches) ? center.pitches : []

  if (pitches.length === 0) {
    return [
      {
        pitchId: null,
        pitchName: null,
        sport: '',
        times: getAvailableSlotsForDateKey(center, dateKey, now, { bookings })
      }
    ]
  }

  const withId = pitches.filter((p) => p?.id != null && String(p.id).trim() !== '')

  if (withId.length === 0) {
    return [
      {
        pitchId: null,
        pitchName: null,
        sport: '',
        times: getAvailableSlotsForDateKey(center, dateKey, now, { bookings })
      }
    ]
  }

  return withId.map((p) => {
    const pid = p.id
    const times = baseSlots.filter((time) => {
      const n = normalizeTimeString(time)
      return !isTimeBookedOnPitch(bookings, dateKey, n, pid)
    })
    return {
      pitchId: pid,
      pitchName: typeof p?.name === 'string' ? p.name : '',
      sport: typeof p?.sport === 'string' ? p.sport : '',
      times
    }
  })
}

export const getPitchSummaries = (center) => {
  const pitches = Array.isArray(center?.pitches) ? center.pitches : []
  return pitches.map((p) => ({
    id: p.id,
    name: p.name,
    sport: p.sport || ''
  }))
}

/**
 * @returns {{ dateKey: string, times: string[] }[]}
 */
export const getUpcomingDaysSlots = (center, numDays = 7, now = new Date()) => {
  const hourly = generateHourlyTimes(center?.openingTime, center?.closingTime)
  if (!hourly.length) return []
  const out = []
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  for (let i = 0; i < numDays; i++) {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    const dateKey = formatDateKey(d)
    const times = i === 0 ? getFutureSlotsForDate(dateKey, hourly, now) : [...hourly]
    if (times.length > 0) out.push({ dateKey, times })
  }
  return out
}
